/** @format */

import React from "react";
import { Alert, Box, Snackbar } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/userSlice";
import useChatBot from "./useChatBot";
import { checkHealth, fetchMyAnalytics, fetchMyFacts } from "./propilotApi";
import { checkMilestoneCrossed, milestoneMessage } from "./propilotEngagement";
import PilotHeader from "./PilotHeader";
import ChatThread from "./ChatThread";
import ChatInput from "./ChatInput";
import AdminMerchantSelect from "./AdminMerchantSelect";
import { useSidebarToggle } from "../../common/SidebarToggleContext";

// Derive a friendly first-name fallback from the email/username when the
// backend hasn't returned a display name yet (analytics still loading).
const deriveNameFromUsername = (username) => {
  if (!username) return "";
  const local = username.includes("@") ? username.split("@")[0] : username;
  const pretty = local.replace(/[._]/g, " ").trim();
  return pretty
    ? pretty
        .split(" ")
        .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
        .join(" ")
    : "";
};

const HEALTH_POLL_MS = 30000;
// Brief delay before logging out so the user can read the auth-failure message.
const LOGOUT_DELAY_MS = 1500;

const ProPilot = () => {
  const { userInfo } = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const accessToken = userInfo?.accessToken;

  // Admin panel has no implicit merchant — the user picks one. Its OID drives
  // the chat call (merchant_list); the backend authorizes it.
  const [selectedMerchant, setSelectedMerchant] = React.useState(null);

  // Collapse the app sidebar while ProPilot is open so the chat + the lg-only
  // "Did you know?" / network fact rails get full width (same as the merchant
  // panel). Restored to the prior state on unmount.
  const { sidebarWidth, setSidebarWidth } = useSidebarToggle();
  const previousSidebarStateRef = React.useRef(sidebarWidth);
  React.useEffect(() => {
    previousSidebarStateRef.current = sidebarWidth;
    setSidebarWidth(true);
    return () => setSidebarWidth(previousSidebarStateRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const authFailedRef = React.useRef(false);

  const handleAuthFailure = React.useCallback(() => {
    if (authFailedRef.current) return;
    authFailedRef.current = true;
    setTimeout(() => {
      dispatch(logout());
    }, LOGOUT_DELAY_MS);
  }, [dispatch]);

  const {
    turns,
    sessionId,
    isFetching,
    analyzingTurn,
    successCount,
    sendMessage,
    analyzeTurn,
    submitTurnFeedback,
    resetSession,
    adminMerchantId,
    merchantAuthError,
  } = useChatBot({ selectedMerchant, onAuthFailure: handleAuthFailure });

  const [healthStatus, setHealthStatus] = React.useState("checking");

  // ── Personalised analytics for the landing screen ───────────────────────────
  const [analytics, setAnalytics] = React.useState(null);
  const [analyticsLoading, setAnalyticsLoading] = React.useState(true);
  const isEmptyThread = turns.length === 0;

  const loadAnalytics = React.useCallback(async () => {
    if (!accessToken) {
      setAnalyticsLoading(false);
      return;
    }
    setAnalyticsLoading(true);
    const data = await fetchMyAnalytics({
      token: accessToken,
      onAuthFailure: handleAuthFailure,
    });
    setAnalytics(data);
    setAnalyticsLoading(false);
  }, [accessToken, handleAuthFailure]);

  // Refresh whenever the user is back on the empty landing screen (initial
  // load + after "New Conversation") so the activity cards stay current.
  React.useEffect(() => {
    if (isEmptyThread) loadAnalytics();
  }, [isEmptyThread, loadAnalytics]);

  // ── Merchant facts for the "Did you know?" / network rails ──────────────────
  // Fetched once a merchant is selected (and again when the admin switches
  // merchant). NOTE: the /api/proship/facts endpoint keys on the NUMERIC
  // merchantId (not the OID that chat uses for RBAC), so we send
  // selectedMerchant.merchantId here. Server-side cache makes repeats ~5ms; a
  // cold merchant can take a few seconds, so the rails appear when ready.
  const factsMerchantId = selectedMerchant?.merchantId ?? null;
  const [merchantFacts, setMerchantFacts] = React.useState(null);
  React.useEffect(() => {
    if (!isEmptyThread || !accessToken || !factsMerchantId) return undefined;
    let cancelled = false;
    setMerchantFacts(null);
    fetchMyFacts({ token: accessToken, adminMerchantId: factsMerchantId }).then(
      (data) => {
        if (!cancelled && data?.ok) setMerchantFacts(data);
      }
    );
    return () => {
      cancelled = true;
    };
  }, [isEmptyThread, accessToken, factsMerchantId]);

  const userName =
    analytics?.name || deriveNameFromUsername(userInfo?.username);

  // ── Milestone celebration (subtle snackbar) ─────────────────────────────────
  const baselineTotalRef = React.useRef(null);
  React.useEffect(() => {
    if (isEmptyThread && analytics?.stats) {
      baselineTotalRef.current = analytics.stats.total_questions ?? 0;
    }
  }, [isEmptyThread, analytics]);

  const [milestoneToast, setMilestoneToast] = React.useState(null);
  const prevSuccessCountRef = React.useRef(0);
  React.useEffect(() => {
    const prev = prevSuccessCountRef.current;
    prevSuccessCountRef.current = successCount;
    if (successCount <= prev || baselineTotalRef.current === null) return;
    const milestone = checkMilestoneCrossed(
      baselineTotalRef.current + prev,
      baselineTotalRef.current + successCount
    );
    if (milestone) setMilestoneToast(milestoneMessage(milestone));
  }, [successCount]);

  React.useEffect(() => {
    let cancelled = false;
    const ping = async () => {
      const ok = await checkHealth();
      if (!cancelled) setHealthStatus(ok ? "ok" : "down");
    };
    ping();
    const timer = setInterval(ping, HEALTH_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, []);

  const handleSendMessage = (text) => sendMessage(text, null);

  const handleSuggestion = (s) => {
    if (!s || !s.text) return;
    sendMessage(s.text, s.action || null);
  };

  const handleNewConversation = () => {
    resetSession();
  };

  // Chat is only usable once a merchant is selected, the backend is up, and
  // the user is authorized for that merchant.
  const isReady =
    !!accessToken &&
    !!adminMerchantId &&
    healthStatus !== "down" &&
    !merchantAuthError;

  return (
    <Box
      sx={{
        // Center within the admin content area (Layout already offsets the
        // sidebar + a ~80px top). Fill the width up to a comfortable cap.
        display: "flex",
        flexDirection: "column",
        width: "100%",
        maxWidth: 1440,
        mx: "auto",
        height: { xs: "calc(100dvh - 90px)", md: "calc(100vh - 130px)" },
        minHeight: { xs: 0, md: 480 },
        pb: { xs: 0, md: 1 },
      }}
    >
      {merchantAuthError && (
        <Alert severity="warning" sx={{ mb: 1, borderRadius: "8px" }}>
          {merchantAuthError}
        </Alert>
      )}
      <Box
        sx={{
          display: "flex",
          flex: 1,
          flexDirection: "column",
          minHeight: 0,
          backgroundColor: "#fff",
          borderRadius: { xs: 0, md: "12px" },
          border: { xs: "none", md: "1px solid #e2e8f0" },
          boxShadow: { xs: "none", md: "0 1px 3px 0 rgba(15,23,42,0.04)" },
          overflow: "hidden",
        }}
      >
        <PilotHeader
          merchantSelector={
            <AdminMerchantSelect
              value={selectedMerchant}
              onChange={setSelectedMerchant}
              disabled={isFetching}
            />
          }
          turnCount={turns.length}
          sessionId={sessionId}
          healthStatus={healthStatus}
          onNewConversation={handleNewConversation}
          disableNew={isFetching}
        />
        <ChatThread
          turns={turns}
          isFetching={isFetching}
          analyzingTurn={analyzingTurn}
          onPromptClick={handleSendMessage}
          onSuggestion={handleSuggestion}
          onAnalyze={analyzeTurn}
          onFeedback={submitTurnFeedback}
          isReady={isReady}
          userName={userName}
          analytics={analytics}
          analyticsLoading={analyticsLoading}
          merchantFacts={merchantFacts}
          sessionId={sessionId}
        />
        <ChatInput
          onSend={handleSendMessage}
          isLoading={isFetching}
          isReady={isReady}
          placeholder={
            adminMerchantId ? undefined : "Select a merchant to begin…"
          }
        />
      </Box>
      <Snackbar
        open={!!milestoneToast}
        autoHideDuration={4000}
        onClose={() => setMilestoneToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        message={milestoneToast}
        sx={{
          bottom: { xs: 88, md: 96 },
          "& .MuiSnackbarContent-root": {
            backgroundColor: "#312e81",
            color: "#fff",
            fontWeight: 600,
            fontSize: 13,
            borderRadius: "12px",
            boxShadow: "0 10px 30px -8px rgba(49,46,129,0.5)",
          },
        }}
      />
    </Box>
  );
};

export default ProPilot;
